<script setup lang="ts">
const { t } = useTexts();
const casino = useCasinoStore();
const route = useRoute('games-provider-game');
const router = useRouter();

const { updateWallet } = useWallet();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { isMobile } = useLayout();
const { getGames } = useCasino();
const { addGame } = useLastGames();
const { userId } = useUserData();
// Fallback client-side: quando o servidor não redirecionou (ex: SPA mode).
// O plugin 06-gameMobilePreview abre o preview ao detectar ?previewGame na query.
const shouldRedirectToHomePreview = ref(false);

const isDirectLaunch = route.query.direct === 'true' && isAuthenticated.value;

// Direct launch: mesma ação do botão JOGAR no MobilePreview — marca o modal
// como aberto pra que o CasinoGameIframe renderize no mobile, limpa o param
// da URL (one-shot) e popula lastPreviewGame para que a seta de voltar da
// games-bar reabra o preview do jogo (mesmo fluxo de quem clicou JOGAR no
// MobilePreview real).
if (import.meta.client && isMobile.value && isDirectLaunch) {
  casino.isGameModalOpen = true;

  const { provider, game } = route.params as Record<string, string>;
  const slug = `${provider}/${game}`;
  const { direct: _direct, ...rest } = route.query;

  router.replace({ path: route.path, query: rest });

  // Fire-and-forget: busca dados completos do jogo pra alimentar
  // rememberAsLastPreview. Sem isso, o backHome da games-bar não tem
  // jogo pra reabrir o preview.
  (async () => {
    try {
      const { getApostatudoApiUrl } = await import(
        '~/utils/apostatudo/get-apostatudo-api-url'
      );

      const res = await fetch(`${getApostatudoApiUrl()}/games?slug=${slug}`, {
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();
      const { rememberAsLastPreview } = useGameMobilePreview();

      rememberAsLastPreview({
        id: data.id,
        slug: data.slug ?? slug,
        name: data.name,
        image: data.image,
        provider: data.provider,
        provider_id: data.provider_id,
        reference: data.reference,
        casino_game_type_id: data.casino_game_type_id,
        demo: data.demo,
        rtp: data.rtp,
        tags: data.tags ?? [],
        ribbon: data.ribbon ?? null,
      } as any);
    } catch {}
  })();
}

if (
  import.meta.client &&
  isMobile.value &&
  !casino.isGameModalOpen &&
  !isDirectLaunch
) {
  shouldRedirectToHomePreview.value = true;

  const { provider, game } = route.params as Record<string, string>;
  const slug = `${provider}/${game}`;

  router.replace({ path: '/', query: { previewGame: slug } });
}

// Rastreia se há histórico de navegação
const hasNavigationHistory = ref(false);

// Função para lidar com o botão voltar
const handleBack = () => {
  // Verifica se pode voltar através do router
  if (hasNavigationHistory.value) {
    router.back();
  } else {
    router.push('/');
  }
};

// Monitora o histórico de navegação
onMounted(() => {
  // Aguarda um momento para garantir que o estado do router está atualizado
  nextTick(() => {
    // Verifica se há histórico usando várias estratégias
    const hasReferrer =
      document.referrer && document.referrer.includes(window.location.host);

    const hasHistoryState = window.history.state?.back;

    hasNavigationHistory.value = hasReferrer || hasHistoryState;
  });
});

const gamesRelated: any = ref([]);
const gameProviders: any = ref([]);

// Faixa de nível acima do frame do jogo, só no desktop (no mobile ela mora na
// games-bar do fullscreen, com outro desenho). Mesmo portão da versão mobile:
// sem gamificação ligada ou sem usuário logado não existe progresso pra mostrar.
const { isGamificationLevelsActived } = storeToRefs(useSmarticoConfigs());
const { isReadyGamefication } = storeToRefs(useSmarticoInit());

const showDesktopLevelProgress = computed(
  () =>
    !isMobile.value &&
    isAuthenticated.value &&
    isGamificationLevelsActived.value &&
    isReadyGamefication.value,
);
// const { isReadySmartico } = storeToRefs(useSmarticoInit());

definePageMeta({
  middleware: [
    async function (to) {
      const { homologation } = useRuntimeConfig().public;
      const { allowedUsers, blockedProviders } = homologation;

      const allowedUsersList = allowedUsers.split(',');
      const blockedProvidersList = blockedProviders.split(',');

      if (!allowedUsersList.length || !blockedProvidersList.length) {
        return;
      }

      const { user } = storeToRefs(useUserDataStore());
      const { $localePath } = useNuxtApp();

      const providerParam = to.params.provider as string;

      const isBlockedProvider = blockedProvidersList.includes(providerParam);

      const isUserAllowed = computed<boolean>(() => {
        const userId = user.value?.id;

        return userId ? allowedUsersList.includes(String(userId)) : false;
      });

      if (isBlockedProvider && !isUserAllowed.value) {
        const localePath = $localePath as ((p: string) => string) | undefined;

        return await navigateTo(localePath ? localePath('/') : '/');
      }
    },
    'router-casino',
  ],
});

const gameDetails = ref<any>({
  isLoading: true,
  id: null,
  url: '',
  name: '...',
  provider: '...',
  provider_id: null,
  provider_slug: '',
  aggregator: '',
  type: null,
  image: false,
  reference: '',
  launchOptions: null,
  demo: 0,
  rtp: 0,
});

const gameDemoMode = ref<Game.Mode>('real');

// TODO: Descomentar isso quando liberar os jogos para anônimos
// const hasGameDemoMode = computed<boolean>(() => {
//   return !!gameDetails.value.demo;
// });

const isGameLoaded = ref(false);
const hasAddedToLastGames = ref(false);

const gameSlug = computed<string>(() => {
  const { provider, game } = route.params;

  return `${provider}/${game}`;
});

const isGameModalOpen = computed({
  get() {
    return casino.isGameModalOpen;
  },
  set(value) {
    casino.isGameModalOpen = value;
  },
});

function fullScreenOn() {
  isGameModalOpen.value = true;
}

function fullScreenOff() {
  updateWallet();
  isGameModalOpen.value = false;
}

async function getRelatedGames() {
  try {
    const filterParams: any = {};

    if (gameDetails.value.id) {
      filterParams.gameId = gameDetails.value.id;
    }

    const res = await getGames(filterParams);

    gamesRelated.value = res.data;
  } catch (error) {
    console.error('Erro ao buscar jogos relacionados:', error);
  }
  try {
    const filterParams = {
      'categories[]': 'all',
    };

    const res = await getGames(filterParams);

    gameProviders.value = [...res.providers];
  } catch (error) {
    console.error('Erro ao buscar provedores dos jogos:', error);
  }
}

async function loadGameDetails() {
  if (!gameSlug.value) return;

  gameDetails.value.isLoading = true;

  try {
    // Busca os dados do jogo usando a API Apostatudo (mesma dos stories)
    const { getApostatudoApiUrl } = await import(
      '~/utils/apostatudo/get-apostatudo-api-url'
    );

    const response = await fetch(
      `${getApostatudoApiUrl()}/games?slug=${gameSlug.value}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    const data = await response.json();

    Object.assign(gameDetails.value, {
      id: data.id,
      reference: data.reference,
      name: data.name || t(`error.game_not_found`),
      provider: data?.provider?.name || '- - -',
      provider_id: data?.provider_id,
      provider_slug: data?.provider?.slug,
      aggregator: data?.provider?.aggregator,
      type: data.casino_game_type_id,
      image: data.image,
      demo: data.demo,
      rtp: data.rtp,
      ribbon: data.ribbon ?? null,
      tags: data.tags ?? [],
      slug: data.slug ?? null,
      // Jogar com Bonus: decide se o gate de carteira aparece. Precisa vir
      // TAMBEM por aqui, e nao so na listagem — quem abre o jogo por link
      // direto ou da F5 nunca passa pela grid, e sem o campo o botao de bonus
      // simplesmente nao existiria nesse caminho.
      bonus_eligible: data.bonus_eligible,
    });

    gameDetails.value.isLoading = false;
    isGameLoaded.value = true;

    await getRelatedGames();

    return true;
  } catch (err) {
    // Cancelamento de navegação (usuário sai antes do fetch completar) ou
    // falha de rede. O caller chama esta função sem await/catch, então
    // tratamos o estado de UI aqui e não re-lançamos para evitar
    // unhandledrejection no Sentry.
    gameDetails.value.name = t(`error.game_not_found`);
    gameDetails.value.provider = 'Erro 404';
    gameDetails.value.isLoading = false;
    isGameLoaded.value = true;

    return false;
  }
}

function onGameOpen(demoMode: Game.Mode) {
  gameDemoMode.value = demoMode;

  fullScreenOn();
}

watch(
  () => gameDetails.value.name,
  () => {
    const title = ref(
      `${gameDetails.value.name} 🍀 ${appName()} | Cassino Online | Casa de Apostas`,
    );

    const description = ref(
      `Jogar ${gameDetails.value.name} com segurança é na ${appName()} 🎰 Cassino Online | Casa de Apostas`,
    );

    useSeo({
      title: title.value,
      description: description.value,
    });
  },
);

watch(gameDemoMode, (mode) => {
  if (!isAuthenticated.value && mode === 'real') {
    openLogin();

    nextTick(() => {
      gameDemoMode.value = 'demo';
    });
  }
});

watch(
  () => isAuthenticated.value,
  () => {
    if (!isAuthenticated.value) {
      gameDemoMode.value = 'demo';
    } else {
      gameDemoMode.value = 'real';
    }

    if (gameSlug.value) {
      if (!isGameLoaded.value) {
        loadGameDetails();
      }
    }
  },
  {
    immediate: true,
  },
);

const { fetchGameStatisticsBySlug } = useGamesStatistics();
const statistics = ref<GameStatisticsByPeriod | null>(null);

// Adiciona o jogo aos últimos jogados quando gameDetails estiver carregado
watch(
  () => gameDetails.value.id,
  (gameId, oldGameId) => {
    // Evita execução duplicada
    if (gameId === oldGameId || hasAddedToLastGames.value) return;

    if (gameId && isAuthenticated.value && userId.value) {
      // Não bloqueia a renderização - executa em background
      addGame(userId.value, gameId).catch(() => {});
      hasAddedToLastGames.value = true;
    } else if (!isAuthenticated.value) {
      // Se não estiver autenticado, marca como true para não bloquear o componente
      hasAddedToLastGames.value = true;
    }
  },
);

onMounted(() => {
  if (gameSlug.value) {
    fetchGameStatisticsBySlug(gameSlug.value).then((res) => {
      statistics.value = res;
    });
  }
});

// Watch for route changes to update statistics on page reload or navigation
watch(
  () => route.fullPath,
  () => {
    if (gameSlug.value) {
      fetchGameStatisticsBySlug(gameSlug.value).then((res) => {
        statistics.value = res;
      });
    }
  },
);

onMounted(() => {
  // Mark that we're on a game page, so when user goes back, filters can be restored
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem('casino_navigation_from_game', 'true');
    } catch (e) {
      // Ignore storage errors
    }
  }
});

onBeforeMount(() => {
  document.body.style.removeProperty('overflow');
});
</script>

<template>
  <section
    v-if="!shouldRedirectToHomePreview"
    id="casino"
  >
    <button
      v-if="isMobile"
      class="mb-1"
      type="button"
      @click="handleBack"
    >
      <svg
        fill="none"
        height="32"
        viewBox="0 0 91 32"
        width="91"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          fill="#3F3F46"
          fill-opacity="0.4"
          height="32"
          rx="12"
          transform="matrix(-1 0 0 1 91 0)"
          width="91"
        />
        <path
          d="M41.7128 12.1H43.2728L39.6128 20.5H38.0768L34.4048 12.1H36.0968L38.8928 18.58L41.7128 12.1ZM47.9895 20.62C45.3855 20.62 43.4655 18.784 43.4655 16.3C43.4655 13.816 45.3855 11.98 47.9895 11.98C50.5935 11.98 52.5135 13.804 52.5135 16.3C52.5135 18.796 50.5935 20.62 47.9895 20.62ZM47.9895 19.252C49.6815 19.252 50.9415 18.016 50.9415 16.3C50.9415 14.584 49.6815 13.348 47.9895 13.348C46.2975 13.348 45.0375 14.584 45.0375 16.3C45.0375 18.016 46.2975 19.252 47.9895 19.252ZM54.1671 20.5V12.1H55.7271V19.18H60.1191V20.5H54.1671ZM62.2695 20.5V13.42H59.4855V12.1H66.6135V13.42H63.8295V20.5H62.2695ZM73.6504 20.5L72.8104 18.556H68.6104L67.7824 20.5H66.1744L69.9544 12.1H71.4904L75.2824 20.5H73.6504ZM69.1384 17.332H72.2944L70.7104 13.66L69.1384 17.332ZM83.5267 20.5H81.8467L80.1307 18.04C80.0227 18.052 79.9147 18.052 79.8067 18.052H77.9107V20.5H76.3507V12.1H79.8067C82.0147 12.1 83.3827 13.228 83.3827 15.088C83.3827 16.36 82.7347 17.296 81.5947 17.74L83.5267 20.5ZM81.8107 15.088C81.8107 14.032 81.1027 13.42 79.7347 13.42H77.9107V16.768H79.7347C81.1027 16.768 81.8107 16.144 81.8107 15.088Z"
          fill="white"
        />
        <path
          d="M14.975 10.9417L9.91664 16L14.975 21.0583"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-miterlimit="10"
          stroke-width="1.5"
        />
        <path
          d="M24.0833 16H10.0583"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-miterlimit="10"
          stroke-width="1.5"
        />
      </svg>
    </button>
    <ClientOnly>
      <LevelsInGameProgressDesktop
        v-if="showDesktopLevelProgress"
        class="mb-3"
      />
    </ClientOnly>

    <CasinoGameIframe
      v-if="!isMobile || (isMobile && isGameModalOpen)"
      :game-demo-mode="gameDemoMode"
      :game-details="gameDetails"
      :game-slug="gameSlug"
      :is-game-modal-open="isGameModalOpen"
      :statistics="statistics"
      @full-screen-off="fullScreenOff()"
    />

    <CasinoGameMobile
      v-if="isMobile"
      :game-details="gameDetails"
      :statistics="statistics"
      @open-game="onGameOpen"
    />

    <CasinoGameDetails
      v-else
      v-model:game-demo-mode="gameDemoMode"
      :game-details="gameDetails"
      :statistics="statistics"
      @full-screen="fullScreenOn"
    />

    <!-- Compliance oficial (ENG-404): +18 + advertência + operador,
         abaixo do frame do jogo e acima das estatísticas -->
    <div class="mx-auto my-6 max-w-[640px] px-4">
      <BaseComplianceInfo />
    </div>

    <CasinoGameStatisticsApostatudo
      v-if="isAuthenticated"
      :statistics="statistics"
    />

    <!-- <CasinoGameRelatedTournament
      v-if="isReadySmartico"
      :tournament="relatedTournaments"
    /> -->

    <!-- <CasinoGameRelated :items="gamesRelated" /> -->
    <CasinoGameLastPlayedPreview
      :exclude-slug="route.params.game as string"
      :space-between="[24, 16, 12]"
    />

    <CasinoGameMaisPagouGrid :exclude-slug="route.params.game as string" />
  </section>
</template>

<style scoped lang="scss">
#casino {
  @apply p-0;
}

/* Desktop: força cards de 150px com aspect ratio mantido nas seções de
   "vistos recentes" (swiper) e "mais pagou" (grid). O CasinoMediaList e o
   PerfilOficialSwiper geram classes hasheadas via CSS module; usamos
   atributo de prefixo + :deep pra alcançar os filhos sem depender do hash. */
@media (min-width: 768px) {
  /* Vistos recentes — swiper. Cada slide é .swiperItem (CSS module).
     Aspect-ratio removido: a imagem interna (.imageWrapper) já reserva sua
     altura via pt-[116%]; o card também precisa caber o badge "Pagou hoje"
     abaixo, então deixamos a altura do slide natural. */
  :deep([class*='swiperItem']) {
    min-width: 150px !important;
    width: 150px !important;
  }

  /* Mais pagou — grid via CasinoMediaList. A grid root tem classe .items
     com grid-template-columns dinâmico; trocamos pra auto-fill 150px. */
  :deep([class*='items']) {
    grid-template-columns: repeat(auto-fill, 150px) !important;
  }
}
</style>
